/**
 * Subscription Blocked Modal Component
 *
 * Shows a message when new subscriptions are temporarily unavailable
 */

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import '../styles/SubscriptionBlockedModal.css';

interface SubscriptionBlockedModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function SubscriptionBlockedModal({ isOpen, onClose }: SubscriptionBlockedModalProps) {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  if (!isOpen || !isMounted) return null;

  const modalContent = (
    <div className="subscription-blocked-overlay" onClick={onClose}>
      <div className="subscription-blocked-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="subscription-blocked-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>

        <div className="subscription-blocked-content">
          <div className="subscription-blocked-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
          </div>

          <h2>New Subscriptions Temporarily Paused</h2>

          <p className="subscription-blocked-message">
            We are temporarily not accepting new customers while we make improvements to our platform.
          </p>

          <p className="subscription-blocked-submessage">
            You will be able to subscribe soon. Thank you for your patience!
          </p>

          <button
            className="subscription-blocked-button"
            onClick={onClose}
          >
            Got It
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
